/*
 * Bundle builder
 * --------------
 * A page-level "pick N products for a set price" builder, done entirely in the
 * theme (no app). The chosen products are added to the cart as normal line items
 * at their normal prices, so Shopify tracks each item's inventory automatically.
 * Each added line carries two line-item properties:
 *   _bundle_type : the section's bundle id (one bundle of this id per cart)
 *   _bundle_id   : a unique id shared by the lines added together (groups them)
 *
 * Layout: products on the left, a sticky summary box on the right with one slot
 * per required item. Slots fill as products are chosen; when the bundle is full
 * the summary button activates ("Checkout") and every unselected product button
 * is disabled. Only one bundle of this `data-bundle-type` may exist in the cart.
 */
class BundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.size = parseInt(this.dataset.bundleSize, 10) || 3;
    this.bundleType = this.dataset.bundleType || this.dataset.sectionId;

    // Optional fixed bundle price (dollars, from the section setting). Stored as
    // integer cents; stamped on each line so the Cart Transform function can
    // rewrite the line prices to total this amount. Blank/0 = charge item prices.
    this.setPriceCents = 0;
    const rawPrice = parseFloat(this.dataset.setPrice);
    if (!isNaN(rawPrice) && rawPrice > 0) {
      this.setPriceCents = Math.round(rawPrice * 100);
    }

    // productId -> { variantId, title, image }
    this.selected = new Map();

    this.grid = this.querySelector('[data-bundle-grid]');
    this.cards = Array.from(this.querySelectorAll('[data-bundle-card]'));
    this.addButton = this.querySelector('[data-bundle-add]');
    this.countEl = this.querySelector('[data-bundle-count]');
    this.notice = this.querySelector('[data-bundle-notice]');
    this.slotsEl = this.querySelector('[data-bundle-slots]');
    this.filtersEl = this.querySelector('[data-bundle-filters]');
    this.noMatchesEl = this.querySelector('[data-bundle-no-matches]');

    this.modal = this.querySelector('[data-bundle-modal]');
    this.modalDialog = this.querySelector('[data-bundle-modal-dialog]');
    // handle -> product JSON, so reopening a product costs nothing
    this.productCache = new Map();
    this.modalOpener = null;

    this.locked = false;
  }

  connectedCallback() {
    if (!this.grid || !this.addButton) return;

    this.cards.forEach((card) => {
      const toggle = card.querySelector('[data-bundle-toggle]');
      if (toggle) toggle.addEventListener('click', () => this.onToggle(card));

      const select = card.querySelector('[data-bundle-variant-select]');
      if (select) select.addEventListener('change', () => this.onVariantChange(card));

      // The card image and the Learn more button both open the dialog. Focus
      // returns to the named button, never to the image, which is hidden from
      // assistive tech.
      const namedTrigger = card.querySelector('.bundle-card__learn-more');
      card.querySelectorAll('[data-bundle-learn-more]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.openModal(card, namedTrigger || trigger));
      });
    });

    this.addButton.addEventListener('click', () => this.addBundle());

    if (this.modal) {
      this.modal.addEventListener('click', (event) => {
        if (event.target.closest('[data-bundle-modal-close]')) this.closeModal();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.modal.hidden) this.closeModal();
      });
    }

    if (this.filtersEl) {
      this.filtersEl.addEventListener('click', (event) => {
        const button = event.target.closest('[data-bundle-filter]');
        if (button) this.applyFilter(button);
      });
    }

    // Remove a product by clicking the × on a filled summary slot.
    if (this.slotsEl) {
      this.slotsEl.addEventListener('click', (event) => {
        const removeBtn = event.target.closest('[data-remove-product]');
        if (!removeBtn) return;
        const card = this.cardFor(removeBtn.dataset.removeProduct);
        if (card) this.deselect(card);
      });
    }

    this.checkExistingBundle();
    this.updateUI();
  }

  /* --- selection ---------------------------------------------------------- */

  cardFor(productId) {
    return this.cards.find((c) => c.dataset.productId === productId);
  }

  variantIdFor(card) {
    const select = card.querySelector('[data-bundle-variant-select]');
    if (select) return select.value;
    const hidden = card.querySelector('[data-bundle-variant-id]');
    return hidden ? hidden.value : null;
  }

  select(card) {
    if (this.selected.size >= this.size) return;
    const variantId = this.variantIdFor(card);
    if (!variantId) return;
    this.selected.set(card.dataset.productId, {
      variantId,
      title: card.dataset.productTitle || '',
      image: card.dataset.productImage || '',
    });
    card.classList.add('is-selected');
    const toggle = card.querySelector('[data-bundle-toggle]');
    if (toggle) toggle.setAttribute('aria-pressed', 'true');
    this.updateUI();
  }

  deselect(card) {
    this.selected.delete(card.dataset.productId);
    card.classList.remove('is-selected');
    const toggle = card.querySelector('[data-bundle-toggle]');
    if (toggle) toggle.setAttribute('aria-pressed', 'false');
    this.updateUI();
  }

  onToggle(card) {
    if (this.locked) return;
    if (this.selected.has(card.dataset.productId)) {
      this.deselect(card);
    } else {
      this.select(card);
    }
  }

  onVariantChange(card) {
    const entry = this.selected.get(card.dataset.productId);
    if (entry) entry.variantId = this.variantIdFor(card);
  }

  /* --- rendering ---------------------------------------------------------- */

  updateUI() {
    const count = this.selected.size;
    const full = count >= this.size;

    if (this.countEl) this.countEl.textContent = count;

    this.cards.forEach((card) => {
      const toggle = card.querySelector('[data-bundle-toggle]');
      if (!toggle) return;
      if (card.classList.contains('bundle-card--soldout')) {
        toggle.disabled = true;
        return;
      }
      // When full, only already-selected cards stay tappable (to remove).
      toggle.disabled = this.locked || (full && !this.selected.has(card.dataset.productId));
    });

    this.addButton.disabled = this.locked || !full;
    this.classList.toggle('is-full', full && !this.locked);

    this.renderSlots();
  }

  renderSlots() {
    if (!this.slotsEl) return;
    const entries = Array.from(this.selected.values());
    let html = '';
    for (let i = 0; i < this.size; i += 1) {
      const entry = entries[i];
      if (entry) {
        const title = this.escape(entry.title);
        const img = entry.image
          ? `<img src="${entry.image}" alt="${title}" loading="lazy">`
          : '';
        html += `
          <li class="bundle-slot is-filled">
            ${img}
            <button type="button" class="bundle-slot__remove" data-remove-product="${this.findProductId(entry)}" aria-label="Remove ${title}">&times;</button>
          </li>`;
      } else {
        html += '<li class="bundle-slot"><span class="bundle-slot__placeholder" aria-hidden="true"></span></li>';
      }
    }
    this.slotsEl.innerHTML = html;
  }

  findProductId(entry) {
    for (const [productId, value] of this.selected.entries()) {
      if (value === entry) return productId;
    }
    return '';
  }

  escape(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[c]));
  }

  /* --- cart --------------------------------------------------------------- */

  async checkExistingBundle() {
    try {
      const res = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await res.json();
      const exists = (cart.items || []).some(
        (item) => item.properties && item.properties._bundle_type === this.bundleType
      );
      if (exists) this.lock();
    } catch (e) {
      /* non-fatal: if the cart can't be read, leave the builder usable */
    }
  }

  lock() {
    this.locked = true;
    if (this.notice) this.notice.hidden = false;
    this.updateUI();
  }

  /*
   * Learn more: shows the product's images, description, price and sizes.
   *
   * The content is fetched from the product JSON endpoint on first open rather
   * than rendered into every card up front -- otherwise every description on the
   * page would ship to every visitor whether or not anyone opens one. The
   * subtitle is not in that payload, so it comes off the card's data attribute.
   */
  async openModal(card, opener) {
    if (!this.modal) return;

    const url = card.dataset.productUrl;
    if (!url) return;

    this.modalOpener = opener || null;

    const titleEl = this.modal.querySelector('[data-bundle-modal-title]');
    const subtitleEl = this.modal.querySelector('[data-bundle-modal-subtitle]');
    const priceEl = this.modal.querySelector('[data-bundle-modal-price]');
    const sizesEl = this.modal.querySelector('[data-bundle-modal-sizes]');
    const descEl = this.modal.querySelector('[data-bundle-modal-description]');
    const mediaEl = this.modal.querySelector('[data-bundle-modal-media]');

    // Show immediately with what the card already knows, so the dialog never
    // opens empty while the request is in flight.
    titleEl.textContent = card.dataset.productTitle || '';
    const subtitle = card.dataset.productSubtitle || '';
    subtitleEl.textContent = subtitle;
    subtitleEl.hidden = !subtitle;
    if (priceEl) priceEl.textContent = '';
    sizesEl.hidden = true;
    descEl.innerHTML = '';
    mediaEl.innerHTML = '';

    this.modal.hidden = false;
    document.body.classList.add('overflow-hidden');
    if (this.modalDialog) this.modalDialog.focus();

    let product = this.productCache.get(url);
    if (!product) {
      try {
        const response = await fetch(url.split('?')[0].replace(/\/$/, '') + '.js', {
          headers: { Accept: 'application/json' },
        });
        product = await response.json();
        this.productCache.set(url, product);
      } catch (e) {
        descEl.textContent = 'Could not load product details.';
        return;
      }
    }

    // The dialog may have been closed, or another product opened, while the
    // request was in flight.
    if (this.modal.hidden || titleEl.textContent !== (card.dataset.productTitle || '')) return;

    titleEl.textContent = product.title || titleEl.textContent;
    if (priceEl) priceEl.textContent = this.formatMoney(product.price);
    descEl.innerHTML = product.description || '';

    const sizes = (product.variants || [])
      .filter((v) => v.title && v.title.toLowerCase() !== 'default title')
      .map((v) => v.title);
    if (sizes.length) {
      sizesEl.textContent = sizes.join(' · ');
      sizesEl.hidden = false;
    }

    this.renderCarousel(mediaEl, product);
  }

  /*
   * Image carousel for the dialog. Scroll-snap does the work, so it swipes
   * natively on touch and the arrows just scroll by one slide; no carousel
   * library is loaded for this. A single image renders as a plain image with no
   * arrows or dots.
   */
  renderCarousel(container, product) {
    const images = product.images || [];
    if (!images.length) return;

    const carousel = document.createElement('div');
    carousel.className = 'bundle-carousel';

    const track = document.createElement('div');
    track.className = 'bundle-carousel__track';

    images.forEach((src, i) => {
      const img = document.createElement('img');
      img.src = src;
      img.loading = i === 0 ? 'eager' : 'lazy';
      // Only the first image names the product; the rest are additional views of
      // the same thing, so naming them all would just repeat it to a screen reader.
      img.alt = i === 0 ? product.title || '' : '';
      track.appendChild(img);
    });

    carousel.appendChild(track);

    if (images.length > 1) {
      const prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'bundle-carousel__nav bundle-carousel__nav--prev';
      prev.setAttribute('aria-label', 'Previous image');
      prev.innerHTML = '&#8249;';

      const next = document.createElement('button');
      next.type = 'button';
      next.className = 'bundle-carousel__nav bundle-carousel__nav--next';
      next.setAttribute('aria-label', 'Next image');
      next.innerHTML = '&#8250;';

      const dots = document.createElement('div');
      dots.className = 'bundle-carousel__dots';

      images.forEach((src, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'bundle-carousel__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Go to image ' + (i + 1));
        dot.addEventListener('click', () => {
          track.scrollTo({ left: track.clientWidth * i, behavior: 'smooth' });
        });
        dots.appendChild(dot);
      });

      const step = (dir) => {
        track.scrollBy({ left: track.clientWidth * dir, behavior: 'smooth' });
      };
      prev.addEventListener('click', () => step(-1));
      next.addEventListener('click', () => step(1));

      let raf = null;
      track.addEventListener('scroll', () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = null;
          const index = Math.round(track.scrollLeft / track.clientWidth);
          Array.from(dots.children).forEach((dot, i) => {
            dot.classList.toggle('is-active', i === index);
          });
        });
      });

      carousel.appendChild(prev);
      carousel.appendChild(next);
      carousel.appendChild(dots);
    }

    container.appendChild(carousel);
  }

  closeModal() {
    if (!this.modal || this.modal.hidden) return;
    this.modal.hidden = true;
    document.body.classList.remove('overflow-hidden');
    if (this.modalOpener) {
      this.modalOpener.focus();
      this.modalOpener = null;
    }
  }

  formatMoney(cents) {
    const amount = (parseInt(cents, 10) || 0) / 100;
    if (window.Shopify && Shopify.formatMoney) {
      try {
        return Shopify.formatMoney(cents, window.moneyFormat);
      } catch (e) {
        /* fall through */
      }
    }
    return '$' + amount.toFixed(2);
  }

  /*
   * Filters the grid by product tag. The filter's own name is the tag: a button
   * labelled "Shampoo" shows products tagged shampoo. The empty filter shows
   * everything.
   *
   * Filtering only hides cards -- it never changes the selection. A product the
   * shopper already picked stays in the bundle while hidden, and is still listed
   * in the summary slots, so switching filters can't silently drop a choice.
   */
  applyFilter(button) {
    const value = (button.dataset.bundleFilter || '').trim().toLowerCase();

    Array.from(this.filtersEl.querySelectorAll('[data-bundle-filter]')).forEach((el) => {
      const active = el === button;
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    let visible = 0;
    this.cards.forEach((card) => {
      const tags = (card.dataset.tags || '').split('|').filter(Boolean);
      const match = !value || tags.indexOf(value) !== -1;
      card.hidden = !match;
      if (match) visible += 1;
    });

    if (this.noMatchesEl) this.noMatchesEl.hidden = visible !== 0;
  }

  async addBundle() {
    if (this.locked || this.selected.size !== this.size) return;

    this.addButton.classList.add('loading');
    this.addButton.disabled = true;

    const bundleId = `${this.bundleType}-${Date.now()}-${Math.floor(Math.random() * 1e4)}`;
    const items = Array.from(this.selected.values()).map((entry) => {
      const properties = { _bundle_type: this.bundleType, _bundle_id: bundleId };
      // When a set price is configured, stamp the bundle total (in cents) and the
      // expected line count on every line. The Cart Transform function groups lines
      // by _bundle_id and rewrites their prices to sum to this total; it uses
      // _bundle_size to refuse a bundle the shopper has since broken up in the cart,
      // which would otherwise charge the full bundle price for fewer items.
      // Omitted when no set price → normal prices.
      if (this.setPriceCents > 0) {
        properties._bundle_total = String(this.setPriceCents);
        properties._bundle_size = String(this.size);
      }
      return { id: entry.variantId, quantity: 1, properties };
    });

    // Ask /cart/add.js for the updated cart-drawer + cart-icon-bubble markup so we
    // can refresh them (mirrors the theme's product-form). Without this the drawer
    // and header count stay stale, so the cart "doesn't work" if the shopper
    // navigates back instead of completing checkout.
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const body = { items };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    try {
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (data.status) {
        // Shopify returned an error (e.g. sold out mid-selection).
        this.showError(data.description || data.message);
        this.addButton.disabled = false;
        this.addButton.classList.remove('loading');
        return;
      }

      // Refresh the cart drawer + header count (without opening the drawer) so the
      // cart is correct if the shopper comes back, then take them to checkout.
      if (cart && data.sections && typeof cart.renderContents === 'function') {
        cart.renderContents(data, false);
      }
      document.dispatchEvent(new Event('customAddToCart'));

      // "Checkout" — take them straight to checkout with the bundle in the cart.
      window.location.href = `${window.Shopify.routes.root}checkout`;
    } catch (e) {
      this.showError('Something went wrong adding your bundle. Please try again.');
      this.addButton.disabled = false;
      this.addButton.classList.remove('loading');
    }
  }

  showError(message) {
    let el = this.querySelector('[data-bundle-error]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'bundle-builder__error';
      el.setAttribute('data-bundle-error', '');
      this.addButton.parentNode.insertBefore(el, this.addButton);
    }
    el.textContent = message;
  }
}

customElements.define('bundle-builder', BundleBuilder);
